import { Theme } from '@mui/material/styles';

/**
 * Type-safe check for dark mode
 */
export const isDarkMode = (theme: Theme): boolean =>
  theme.palette.mode === 'dark';

/**
 * Get theme-aware gradient value
 * @param theme - MUI Theme object
 * @param light - Gradient for light mode
 * @param dark - Gradient for dark mode
 */
export const getThemeGradient = (
  theme: Theme,
  light: string,
  dark: string
): string => isDarkMode(theme) ? dark : light;

/**
 * Get theme-aware rgba background with proper opacity
 * Dark mode uses slightly higher opacity (0.9) for better contrast with blur
 * Light mode uses 0.8 opacity
 */
export const getGlassBackground = (
  theme: Theme,
  options?: { lightOpacity?: number; darkOpacity?: number }
): string => {
  const { lightOpacity = 0.8, darkOpacity = 0.9 } = options ?? {};
  return isDarkMode(theme)
    ? `rgba(26, 26, 26, ${darkOpacity})`
    : `rgba(255, 255, 255, ${lightOpacity})`;
};

/**
 * Standardized icon sizing scale
 * @see docs/web/ux-guidelines.md#icon-sizing-scale
 *
 * Usage:
 * ```tsx
 * <HomeIcon sx={{ fontSize: iconSizes.md }} />
 * <BuildingIcon sx={{ fontSize: iconSizes.xl }} />
 * ```
 */
export const iconSizes = {
  xs: 16,  // Inline icons (chips, badges, inline text)
  sm: 20,  // Secondary icons (form helpers, tooltips)
  md: 24,  // Standard icons (navigation, actions)
  lg: 32,  // Emphasized icons (section headers)
  xl: 40,  // Page header icons (max for headers)
  '2xl': 56,  // Empty state icons
  '3xl': 72,  // Full-page illustrations
} as const;

export type IconSize = keyof typeof iconSizes;

/**
 * Get icon size in pixels
 * @param size - Icon size token
 * @returns Size in pixels
 *
 * @example
 * getIconSize('md') // 24
 * getIconSize('xl') // 40
 */
export const getIconSize = (size: IconSize): number => iconSizes[size];
